import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';
import { toastService } from '../../services/toastService';
import { inventoryApi, type DamageResolveAction, type DamageRow } from '../../services/inventoryApi';

/**
 * DAMAGE LIFECYCLE — inline "Resolve ▾" / "Cancel report" actions for ONE
 * open damage row. Reused everywhere a damage report is visible so the
 * action always lives right next to what it acts on — the Damage tab's
 * table, the Unit page's Active-damage card, and the Stock tab's per-unit
 * ⚠ badge popover — never a separate page to get one thing done.
 *
 * Renders nothing for a row that is not 'open' (resolved/cancelled rows show
 * their `resolution` instead — the caller renders that, not this component).
 * Custom dropdown + confirm modal only — no native <select>/confirm().
 */

const RESOLVE_OPTIONS: { id: Exclude<DamageResolveAction, 'cancel'>; label: string; hint: string }[] = [
  { id: 'vendor_credit', label: 'Vendor credit', hint: 'Resolved — unit state unchanged' },
  { id: 'returned_to_vendor', label: 'Returned to vendor', hint: 'Resolved — unit → retired' },
  { id: 'repaired_to_stock', label: 'Repaired → stock', hint: 'Resolved — unit → in stock' },
  { id: 'written_off', label: 'Write off', hint: 'Resolved — unit → retired' },
];

const ACTION_LABEL: Record<DamageResolveAction, string> = {
  cancel: 'Cancel report',
  vendor_credit: 'Vendor credit',
  returned_to_vendor: 'Returned to vendor',
  repaired_to_stock: 'Repaired → stock',
  written_off: 'Write off',
};

export interface DamageResolveResult {
  damage: DamageRow;
  unit?: Record<string, any>;
  note?: string;
}

interface Props {
  damage: Pick<DamageRow, 'unitId' | 'createdAt' | 'status' | 'reason'>;
  onResolved: (result: DamageResolveResult) => void;
  className?: string;
  /** Smaller footprint for tight spaces (Stock tab popover). */
  compact?: boolean;
}

const MENU_HEIGHT_ESTIMATE = 220; // 4 options ≈ this; used only to flip above near the viewport bottom

const DamageResolveActions: React.FC<Props> = ({ damage, onResolved, className, compact }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  // Viewport coordinates for the portal'd menu — measured from the trigger
  // button when it opens. The menu used to be position:absolute inside the
  // card, and every later card painted OVER it (own stacking context), so it
  // now renders through a portal at fixed coordinates instead.
  const [menuPos, setMenuPos] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [pending, setPending] = useState<DamageResolveAction | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleMenu = () => {
    if (menuOpen) {
      setMenuOpen(false);
      return;
    }
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const right = Math.max(8, window.innerWidth - r.right);
    if (r.bottom + MENU_HEIGHT_ESTIMATE > window.innerHeight) {
      setMenuPos({ bottom: Math.max(8, window.innerHeight - r.top + 4), right });
    } else {
      setMenuPos({ top: r.bottom + 4, right });
    }
    setMenuOpen(true);
  };

  // Fixed coordinates go stale on scroll/resize — just close.
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menuOpen]);

  // A model-scoped (bulk quantity) damage row carries no unitId — it is not
  // resolvable through this endpoint (which moves a UNIT's state), so it
  // renders read-only everywhere this component is used, same as a
  // non-open row.
  if (damage.status !== 'open' || !damage.unitId) return null;

  const closeModal = () => {
    if (saving) return;
    setPending(null);
    setNotes('');
    setError(null);
  };

  const confirm = async () => {
    if (!pending) return;
    setSaving(true);
    setError(null);
    try {
      const res = await inventoryApi.resolveDamage({
        unitId: damage.unitId,
        createdAt: damage.createdAt,
        action: pending,
        notes: notes.trim() || undefined,
      });
      toastService.success(res.note ? `${ACTION_LABEL[pending]} recorded — ${res.note}` : `${ACTION_LABEL[pending]} recorded.`);
      onResolved(res);
      setPending(null);
      setNotes('');
    } catch (e: any) {
      setError(e?.message || 'Failed to resolve this report');
    } finally {
      setSaving(false);
    }
  };

  const btnCls = compact
    ? 'px-2 py-1 rounded text-[10px] font-semibold'
    : 'px-2.5 py-1.5 rounded-lg text-[11px] font-semibold';

  return (
    <div className={`relative inline-flex items-center gap-1.5 ${className || ''}`}>
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={toggleMenu}
          className={`flex items-center gap-1 border border-stone-300 bg-white text-stone-700 ${btnCls}`}
        >
          Resolve <ChevronDown size={11} />
        </button>
        {menuOpen && menuPos && createPortal(
          <>
            <div className="fixed inset-0 z-[9490]" onClick={() => setMenuOpen(false)} />
            <div
              className="fixed z-[9500] w-56 rounded-lg border border-stone-200 bg-white shadow-xl overflow-hidden"
              style={{ top: menuPos.top, bottom: menuPos.bottom, right: menuPos.right }}
            >
              {RESOLVE_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => { setMenuOpen(false); setPending(o.id); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-stone-50 border-b border-stone-100 last:border-b-0"
                >
                  <div className="font-semibold text-stone-800">{o.label}</div>
                  <div className="text-[10px] text-stone-500 mt-0.5">{o.hint}</div>
                </button>
              ))}
            </div>
          </>,
          document.body,
        )}
      </div>
      <button
        type="button"
        onClick={() => setPending('cancel')}
        className={`border border-stone-300 bg-white text-stone-500 ${btnCls}`}
      >
        Cancel report
      </button>

      {pending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeModal}>
          <div
            className="w-full max-w-sm rounded-2xl bg-white text-gray-900 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between bg-black px-4 py-3 text-white">
              <h3 className="text-sm font-bold">{ACTION_LABEL[pending]}</h3>
              <button onClick={closeModal} disabled={saving}><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-stone-600 font-mono">{damage.unitId}</p>
              <p className="text-xs text-stone-600">{damage.reason}</p>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder={pending === 'cancel' ? 'Why was this a mistaken report?' : 'e.g. RMA #, credit memo, repair ticket'}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-xs resize-none"
                />
              </div>
              {error && <p className="text-xs text-red-700">{error}</p>}
            </div>
            <div className="flex justify-end gap-2 border-t border-stone-200 px-4 py-3">
              <button onClick={closeModal} disabled={saving} className="px-3 py-1.5 rounded-lg border border-stone-300 text-xs font-medium text-stone-700">
                Back
              </button>
              <button
                onClick={confirm}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold disabled:opacity-50"
                style={{ background: '#DC143C' }}
              >
                {saving ? <RubyGemMark size={12} drawing color="currentColor" /> : null} Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Compact "what happened" line for a RESOLVED/CANCELLED row — action, by whom, when, and any notes. Used everywhere a closed-out damage report is shown. */
export const DamageResolutionNote: React.FC<{ damage: Pick<DamageRow, 'resolution'> }> = ({ damage }) => {
  if (!damage.resolution) return null;
  const r = damage.resolution;
  return (
    <div className="text-[10.5px] text-stone-500">
      <span className="font-semibold text-stone-700">{ACTION_LABEL[r.action === 'cancelled' ? 'cancel' : r.action]}</span>
      {' · by '}{r.by}{' · '}{new Date(r.at).toLocaleString()}
      {r.notes && <div className="text-stone-400 mt-0.5">{r.notes}</div>}
    </div>
  );
};

export default DamageResolveActions;
